
import React, { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Eye, EyeOff } from 'lucide-react';

interface ResetPasswordDialogProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  userEmail: string;
  onSuccess?: (email: string) => void;
}

export const ResetPasswordDialog: React.FC<ResetPasswordDialogProps> = ({
  isOpen,
  onClose,
  userId,
  userName,
  userEmail,
  onSuccess,
}) => {
  const { toast } = useToast();
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const handleClose = () => {
    setNewPassword('');
    setShowPassword(false);
    onClose();
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast({
        title: "Password required",
        description: "Please enter a password with at least 8 characters",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsResetting(true);
      const { error } = await supabase.functions.invoke('admin-reset-password', {
        body: { userId, newPassword },
      });

      if (error) throw error;

      toast({
        title: "Password reset successful",
        description: `Password has been reset for ${userName}`,
      });
      
      onSuccess?.(userEmail);
      handleClose();
    } catch (error) {
      toast({
        title: "Failed to reset password",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-md mx-4">
        <DialogHeader>
          <DialogTitle>Reset Password</DialogTitle>
          <DialogDescription className="space-y-2">
            <span className="block text-sm">
              Set a new password for{' '}
              <span className="font-semibold text-foreground">{userName}</span>
            </span>
            <span className="text-xs font-mono bg-muted px-2 py-1 rounded inline-block truncate max-w-full">
              {userEmail}
            </span>
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div className="space-y-2">
            <Label htmlFor="new-password" className="block text-sm font-medium">
              New Password
            </Label>
            <div className="relative">
              <Input
                id="new-password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter new password (min. 8 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="pr-10"
                autoComplete="new-password"
                autoFocus
                hasError={newPassword.length > 0 && newPassword.length < 8}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {newPassword && newPassword.length < 8 && (
              <p className="text-xs text-destructive flex items-center gap-1.5 mt-1.5">
                <span className="text-base leading-none">●</span>
                Password must be at least 8 characters
              </p>
            )}
            {newPassword && newPassword.length >= 8 && (
              <p className="text-xs text-green-600 dark:text-green-500 flex items-center gap-1.5 mt-1.5">
                <span className="text-base leading-none">✓</span>
                Password meets requirements
              </p>
            )}
          </div>
        </DialogBody>

        <DialogFooter className="gap-2 flex-col-reverse sm:flex-row sm:justify-end">
          <Button 
            variant="outline" 
            onClick={handleClose} 
            disabled={isResetting}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleResetPassword} 
            disabled={!newPassword || isResetting}
            className="w-full sm:w-auto"
          >
            {isResetting ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Resetting...
              </span>
            ) : (
              "Reset Password"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
